import React, { useRef } from "react";
import { Camera, ImagePlus, RefreshCw, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MAX_EDGE = 1600; // px — keeps phone photos fast to upload on any connection

async function downscale(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return file; // HEIC etc. pass through
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
    if (scale >= 1) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.82));
    return blob ? new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file;
  } catch {
    return file;
  }
}

/**
 * Optional quick-photo step shared by both booking flows.
 * value: null | { previewUrl, name, status: 'uploading'|'ready'|'error', url? }
 * onChange(next): parent stores it and includes `value.url` in the booking payload
 * only when status === 'ready'.
 */
export default function PhotoCapture({ idPrefix, heading, subtext, value, onChange }) {
  const takeRef = useRef(null);
  const uploadRef = useRef(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (value?.previewUrl) URL.revokeObjectURL(value.previewUrl);
    const previewUrl = URL.createObjectURL(file);
    onChange({ previewUrl, name: file.name, status: "uploading" });
    try {
      const small = await downscale(file);
      const fd = new FormData();
      fd.append("file", small);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ previewUrl, name: file.name, status: "ready", url: data.url });
    } catch (err) {
      onChange({ previewUrl, name: file.name, status: "error" });
      toast.error(
        err.response?.data?.detail || "Couldn't attach the photo — no problem, you can still book without it."
      );
    }
  };

  const remove = () => {
    if (value?.previewUrl) URL.revokeObjectURL(value.previewUrl);
    onChange(null);
  };

  return (
    <div
      className="rounded-2xl border border-dashed border-blue-500/30 bg-blue-500/[0.04] p-5 sm:p-6"
      data-testid={`${idPrefix}-photo-section`}
    >
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-blue-400">
        Optional · Want to make this easier?
      </div>
      <h3 className="font-display text-lg sm:text-xl font-bold text-white mt-2">{heading}</h3>
      <p className="text-xs sm:text-sm text-white/55 mt-1.5 leading-relaxed">{subtext}</p>

      {!value ? (
        <div className="mt-4 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => takeRef.current?.click()}
            className="flex items-center justify-center gap-2 h-14 rounded-xl bg-[#0B132B] border border-white/10 text-white font-semibold text-sm hover:border-blue-500/50 transition-colors"
            data-testid={`${idPrefix}-photo-take-btn`}
          >
            <Camera className="h-5 w-5 text-blue-400" /> Take Photo
          </button>
          <button
            type="button"
            onClick={() => uploadRef.current?.click()}
            className="flex items-center justify-center gap-2 h-14 rounded-xl bg-[#0B132B] border border-white/10 text-white font-semibold text-sm hover:border-blue-500/50 transition-colors"
            data-testid={`${idPrefix}-photo-upload-btn`}
          >
            <ImagePlus className="h-5 w-5 text-blue-400" /> Upload Photo
          </button>
        </div>
      ) : (
        <div className="mt-4">
          <div className="relative rounded-xl overflow-hidden border border-white/10 bg-[#0B132B]">
            <img
              src={value.previewUrl}
              alt="Preview of the photo attached to this booking request"
              className="w-full max-h-64 object-cover"
              data-testid={`${idPrefix}-photo-preview`}
            />
            <div
              className={`absolute top-2.5 left-2.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-[0.15em] ${
                value.status === "ready"
                  ? "bg-emerald-500/90 text-[#050914]"
                  : value.status === "uploading"
                    ? "bg-blue-600/90 text-white"
                    : "bg-amber-500/90 text-[#050914]"
              }`}
              data-testid={`${idPrefix}-photo-status`}
            >
              {value.status === "ready" && (<><Check className="h-3 w-3" /> Attached</>)}
              {value.status === "uploading" && (<><Loader2 className="h-3 w-3 animate-spin" /> Attaching…</>)}
              {value.status === "error" && "Couldn't attach — book anyway"}
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <span className="text-xs text-white/40 truncate flex-1">{value.name}</span>
            <button
              type="button"
              onClick={() => uploadRef.current?.click()}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 px-3 py-2 rounded-md border border-white/10 hover:border-blue-500/40 transition-colors"
              data-testid={`${idPrefix}-photo-replace-btn`}
            >
              <RefreshCw className="h-3.5 w-3.5" /> Replace
            </button>
            <button
              type="button"
              onClick={remove}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-400 hover:text-red-300 px-3 py-2 rounded-md border border-white/10 hover:border-red-500/40 transition-colors"
              data-testid={`${idPrefix}-photo-remove-btn`}
            >
              <X className="h-3.5 w-3.5" /> Remove
            </button>
          </div>
        </div>
      )}

      {/* Take Photo opens the device camera on mobile when supported */}
      <input ref={takeRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${idPrefix}-photo-take-input`} />
      <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${idPrefix}-photo-upload-input`} />

      <p className="mt-3.5 text-[11px] text-white/40 leading-relaxed">
        Totally optional — you can book without a photo. Photos only help us prepare; they don't change
        your package or price. Anything extra is always discussed with and approved by you first.
      </p>
    </div>
  );
}
